import React, { useState } from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en-gb';
import { contractActionFacts, startDelayLimit, startDelayEarliest } from '../../../utils/contractActionFacts';
import { toPickerValue } from '../../../utils/dateOnly';

dayjs.extend(utc);

const EmployeeStartDelayed = ({ employee, contract = null, onValidChange }) => {
  // ADR-187 — the acting contract's own facts (passed by ContractActionModals);
  // the person's flat fields are the PRIMARY's and are read only through the
  // resolver, when this is the primary or the payload has no contracts[].
  const facts = contract || contractActionFacts(employee, null);
  const [formData, setFormData] = useState({
    newStartDate: null,
    reason: ''
  });

  const [touched, setTouched] = useState({
    newStartDate: false,
    reason: false
  });

  // ADR-136 phase 4 (§2a settlement #5): for a fixed-term joiner the end date
  // stays FIXED when the start is delayed — the term shortens. The delayed start
  // must therefore remain strictly before the scheduled end.
  const contractEndDate = facts.endDate;
  // ADR-188 D2 — the tightest of: this contract's own end, the primary's end
  // (a secondary), another open contract's start (the primary). The server
  // refuses the same (hire.start-delayed).
  const limit = startDelayLimit(employee, facts);
  // ADR-188 D2d — and a delay moves the start LATER (never earlier or the same
  // day): the server refuses the same (NOT_A_DELAY)
  const earliest = startDelayEarliest(facts, dayjs().format('YYYY-MM-DD'));
  const isAfterCurrent = (dateStr) => !dateStr || dateStr >= earliest.earliestYmd;
  const startsBeforeEnd = (dateStr) => (
    isAfterCurrent(dateStr) && (!limit.latestYmd || !dateStr || dateStr <= limit.latestYmd)
  );

  const handleDateChange = (newValue) => {
    const newDate = newValue && typeof newValue.isValid === 'function' && newValue.isValid()
      ? newValue.format('YYYY-MM-DD')
      : null;

    const newFormData = {
      ...formData,
      newStartDate: newDate
    };
    setFormData(newFormData);
    setTouched(prev => ({
      ...prev,
      newStartDate: true
    }));

    // Valid if date and reason are provided, and the delayed start stays before
    // any scheduled contract end
    const isValid = Boolean(newDate && formData.reason && startsBeforeEnd(newDate));
    onValidChange(isValid, newFormData);
  };

  const handleInputChange = (field) => (event) => {
    const newFormData = {
      ...formData,
      [field]: event.target.value
    };
    setFormData(newFormData);
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Valid if date and reason are provided, and the delayed start stays before
    // any scheduled contract end
    const isValid = Boolean(
      formData.newStartDate && event.target.value && startsBeforeEnd(formData.newStartDate)
    );
    onValidChange(isValid, newFormData);
  };

  const shouldShowError = (field) => {
    return touched[field];
  };

  React.useEffect(() => {
    // Initialize as invalid
    onValidChange(false, formData);
  }, []);

  // The start being delayed is the pending contract start (episode
  // HIRE_FINALIZED). For an ordinary joiner it equals the hire date; after an
  // ADR-138 "keep current hire date" rehire the two differ, and contractStartDate
  // is the one this action moves.
  const currentStartDate = facts.startDate;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
        <Alert severity="info">
          This moves the person&apos;s upcoming start to a later date — the scheduled hire and
          the employment episode are re-dated, and the contract number and comments are kept.
          The hire date on the record follows only when it tracks this contract&apos;s start;
          a deliberately kept earlier date is left untouched.
          {contractEndDate && (
            <> For this <strong>fixed-term</strong> contract the end date stays fixed — delaying
            the start shortens the term.</>
          )}
        </Alert>
        {currentStartDate && (
          <Typography variant="body2" color="text.secondary">
            Current start date: <strong>{dayjs.utc(currentStartDate).format('DD/MM/YYYY')}</strong>
          </Typography>
        )}
        {contractEndDate && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: -2 }}>
            Contract end date: <strong>{dayjs.utc(contractEndDate).format('DD/MM/YYYY')}</strong>
            {' '}— stays fixed; delaying the start shortens the term
          </Typography>
        )}
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <DatePicker
            label="New Start Date"
            value={toPickerValue(formData.newStartDate)}
            onChange={handleDateChange}
            minDate={dayjs(earliest.earliestYmd)}
            maxDate={limit.latestYmd ? dayjs(limit.latestYmd) : undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: (shouldShowError('newStartDate') && !formData.newStartDate)
                  || Boolean(formData.newStartDate && !startsBeforeEnd(formData.newStartDate)),
                helperText: formData.newStartDate && !startsBeforeEnd(formData.newStartDate)
                  ? (isAfterCurrent(formData.newStartDate) ? limit.reason : earliest.reason)
                  : (shouldShowError('newStartDate') && !formData.newStartDate
                    ? 'New start date is required and must be in the future'
                    : 'The new date when the employee will start'),
                inputProps: {
                  placeholder: 'DD/MM/YYYY'
                }
              }
            }}
          />
        </LocalizationProvider>

        <TextField
          fullWidth
          label="Reason"
          value={formData.reason}
          onChange={handleInputChange('reason')}
          multiline
          rows={4}
          required
          error={shouldShowError('reason') && !formData.reason}
          helperText={shouldShowError('reason') && !formData.reason
            ? 'Reason is required'
            : 'Reason for delaying the start date'}
          placeholder="Enter reason for delay..."
        />
      </Box>
    </Box>
  );
};

export default EmployeeStartDelayed;
